import type { Transport } from "../../../transport";
import type * as Domain from "../../domain";

import type { Protocol, SubscriberId, Version } from "./protocol";
import type { GridStore, GridSnapshot } from "../types";

/**
 * ReplicatedGrid wraps a GridStore and keeps it in sync via a Transport.
 *
 * Strategy: Lazy Sync
 * - Starts assuming Version 0.
 * - Applies sequential updates immediately (v1, v2, v3).
 * - If a version gap is detected (e.g., receiving v10 when at v0), it halts updates
 * from that peer and requests a full snapshot to catch up.
 */
export class ReplicatedGrid implements GridStore {
  private readonly id: SubscriberId;
  private version: Version = 0 as Version;

  // Track the latest version we have seen from every other node
  private subscribers = new Map<SubscriberId, Version>();

  // If we are waiting for a snapshot from a peer, we ignore their stream of updates
  private pendingSnapshots = new Set<SubscriberId>();

  private appliedListeners = new Set<(from: SubscriberId, version: Version) => void>();

  readonly unsubscribe: () => void;

  stats = new ReplicateStats();

  constructor(
    private options: { protocol: Protocol; store: GridStore; channel: Transport<Uint8Array>; name?: string },
  ) {
    this.id = `node_${Math.random().toString(36).slice(2)}` as SubscriberId;

    this.unsubscribe = this.options.channel.onMessage((bytes) => {
      // track inbound traffic
      this.stats.bytes_received += bytes.byteLength;
      const startDecode = performance.now();
      this.options.protocol.decode(bytes, {
        onUpdate: (from, version, event) => this.handleUpdateMsg(from, version, event),
        onSnapshotRequest: (from, to) => this.handleSnapshotRequest(from, to),
        onSnapshotResponse: (from, to, version, snapshot) => this.handleSnapshotResponse(to, from, version, snapshot),
      });
      this.stats.decode_time += performance.now() - startDecode;
    });
  }

  /** Request a full snapshot from any existing peer to catch up immediately */
  requestSnapshot() {
    this.send(() => this.options.protocol.encodeSnapshotRequest(this.id));
  }

  applyUpdate(event: Domain.GridUpdateEvent): void {
    const start = performance.now();
    this.options.store.applyUpdate(event);
    this.stats.apply_time_local += performance.now() - start;

    this.version = (this.version + 1) as Version;
    this.stats.ops_local++;

    this.send(() => this.options.protocol.encodeUpdate(this.id, this.version, event));
  }

  getVersion(): number {
    return this.version;
  }

  /** Fires after a peer's update or snapshot has been applied, with the peer's version we are now at. */
  onApplied(cb: (from: SubscriberId, version: Version) => void) {
    this.appliedListeners.add(cb);
    return () => {
      this.appliedListeners.delete(cb);
    };
  }

  readCell(product: Domain.ProductId, field: Domain.Field, row: Domain.Offset | string) {
    return this.options.store.readCell(product, field, row);
  }

  getSnapshot() {
    return this.options.store.getSnapshot();
  }

  restoreSnapshot(snapshot: GridSnapshot) {
    this.options.store.restoreSnapshot(snapshot);
  }

  clear() {
    this.options.store.clear();
  }

  debugDecode(data: Uint8Array) {
    // biome-ignore lint/suspicious/noExplicitAny: because
    let ev: any = null;
    this.options.protocol.decode(data, {
      onUpdate: (from, version, event) => {
        ev = { type: "UPDATE", data: { from, version, event } };
      },
      onSnapshotRequest: (from, to) => {
        ev = { type: "SNAPSHOT_REQUEST", data: { from, to } };
      },
      onSnapshotResponse: (from, to, version, snapshot) => {
        ev = { type: "SNAPSHOT_RESPONSE", data: { from, to, version, snapshot } };
      },
    });
    return ev;
  }

  /** Returns replication topology diagnostics for debugging */
  getDiagnostics = () => ({
    ownId: this.id,
    version: this.version,
    peers: Array.from(this.subscribers.entries()).map(([id, ver]) => ({
      id,
      version: ver,
      isPendingSnapshot: this.pendingSnapshots.has(id),
      lag: this.version - ver,
    })),
    pendingSnapshotCount: this.pendingSnapshots.size,
  });

  private handleUpdateMsg(from: SubscriberId, version: Version, event: Domain.GridUpdateEvent) {
    // logger.log(`handleUpdateMsg ${this.options.name}`);
    // 1. If we are already waiting for a snapshot from this peer, ignore their updates
    if (this.pendingSnapshots.has(from)) return;

    const lastVersion = this.subscribers.get(from) ?? 0;

    // 2. Check for Consistency
    if (version !== lastVersion + 1) {
      this.pendingSnapshots.add(from);
      this.send(() => this.options.protocol.encodeSnapshotRequest(this.id, from));
      return;
    }

    // 4. Happy Path: Sequential update
    this.subscribers.set(from, version);

    const start = performance.now();
    this.options.store.applyUpdate(event);
    this.stats.apply_time_remote += performance.now() - start;
    this.stats.ops_remote++;
    this.notifyApplied(from, version);
  }

  private handleSnapshotRequest(from: SubscriberId, to?: SubscriberId) {
    if (to !== undefined && to !== this.id) return;

    const start = performance.now();
    const snapshot = this.options.store.getSnapshot();
    this.stats.snapshot_time += performance.now() - start;

    this.send(() => this.options.protocol.encodeSnapshotResponse(this.id, from, this.version, snapshot));
  }

  private handleSnapshotResponse(to: SubscriberId, from: SubscriberId, version: Version, snapshot: GridSnapshot) {
    if (to !== this.id) return;

    // We are now synced with this peer
    this.pendingSnapshots.delete(from);
    this.subscribers.set(from, version);

    // Hard reset the local store with the authoritative snapshot
    this.options.store.restoreSnapshot(snapshot);
    this.notifyApplied(from, version);
  }

  private notifyApplied(from: SubscriberId, version: Version) {
    for (const cb of this.appliedListeners) cb(from, version);
  }

  private send(encode: () => Uint8Array) {
    try {
      const start = performance.now();
      const encoded = encode();
      this.stats.encode_time += performance.now() - start;
      this.stats.bytes_sent += encoded.byteLength;
      this.options.channel.send(encoded);
    } catch (e) {
      console.error(`[${this.options.name}] Failed to send grid message`, e);
    }
  }
}

export class ReplicateStats {
  ops_local = 0;
  ops_remote = 0;
  bytes_sent = 0;
  bytes_received = 0;
  encode_time = 0;
  decode_time = 0;
  snapshot_time = 0;
  apply_time_local = 0;
  apply_time_remote = 0;
}
